/**
 * AWS Configuration
 * Centralized configuration for all AWS services used in the application
 */

export interface AWSConfig {
  region: string;
  accountId: string;
  ivs: {
    region: string;
    chat: {
      region: string;
    };
  };
  endpoints: {
    playback: string;
    ingest: string;
  };
}

// Default configuration - can be overridden by environment variables
const defaultConfig: AWSConfig = {
  region: 'us-east-1',
  accountId: '123456789012', // Default for mock data
  ivs: {
    region: 'us-east-1',
    chat: {
      region: 'eu-west-1',
    },
  },
  endpoints: {
    playback: 'playback.live-video.net',
    ingest: 'global-contribute.live-video.net',
  },
};

// Runtime configuration with environment variable overrides
export const awsConfig: AWSConfig = {
  region: process.env.NEXT_PUBLIC_AWS_REGION || defaultConfig.region,
  accountId: process.env.NEXT_PUBLIC_AWS_ACCOUNT_ID || defaultConfig.accountId,
  ivs: {
    region: process.env.NEXT_PUBLIC_IVS_REGION || defaultConfig.ivs.region,
    chat: {
      region: process.env.NEXT_PUBLIC_IVS_CHAT_REGION || defaultConfig.ivs.chat.region,
    },
  },
  endpoints: {
    playback: process.env.NEXT_PUBLIC_IVS_PLAYBACK_ENDPOINT || defaultConfig.endpoints.playback,
    ingest: process.env.NEXT_PUBLIC_IVS_INGEST_ENDPOINT || defaultConfig.endpoints.ingest,
  },
};

/**
 * Helper functions for generating AWS ARNs
 */
export const generateArn = {
  ivsStage: (stageId: string): string => 
    `arn:aws:ivs:${awsConfig.ivs.region}:${awsConfig.accountId}:stage/${stageId}`,
    
  ivsChannel: (channelId: string): string => 
    `arn:aws:ivs:${awsConfig.ivs.region}:${awsConfig.accountId}:channel/${channelId}`,
    
  ivsChatRoom: (chatRoomId: string): string => 
    `arn:aws:ivschat:${awsConfig.ivs.chat.region}:${awsConfig.accountId}:room/${chatRoomId}`,
    
  playbackUrl: (channelId: string): string => 
    `https://example.${awsConfig.ivs.region}.${awsConfig.endpoints.playback}/api/video/v1/${awsConfig.ivs.region}.${awsConfig.accountId}.channel.${channelId}.m3u8`,
};

/**
 * Development/Mock configuration helper
 */
export const isMockEnvironment = (): boolean => {
  return process.env.NODE_ENV === 'development' || 
         process.env.NEXT_PUBLIC_USE_MOCK_DATA === 'true';
};

/**
 * Validate AWS configuration on startup
 */
export const validateAWSConfig = (): void => {
  const errors: string[] = [];

  if (!awsConfig.region) {
    errors.push('AWS region is required');
  }

  if (!awsConfig.accountId || awsConfig.accountId === '123456789012') {
    console.warn('Using default/mock AWS account ID. Set NEXT_PUBLIC_AWS_ACCOUNT_ID for production.');
  }

  if (!awsConfig.ivs.region) {
    errors.push('IVS region is required');
  }

  if (!awsConfig.ivs.chat.region) {
    errors.push('IVS Chat region is required');
  }

  if (errors.length > 0) {
    throw new Error(`AWS Configuration validation failed: ${errors.join(', ')}`);
  }
};

export default awsConfig;